import React, { useContext, useState } from 'react';

import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  makeStyles
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';

import { ReactComponent as ConfirmIcon } from 'pages/Teachers/ClassroomInsights/assets/check.svg';
import CheckGradeIcon from 'pages/Teachers/ClassroomInsights/components/CheckGradeIcon';
import { NUMBER_VALUE_TYPES } from 'pages/Teachers/ClassroomInsights/constants';
import { MASTERY_LEVEL_TO_LABEL_MAPPER_SELECT } from 'pages/Teachers/ClassroomInsights/constants';
import { UNABLE_TO_DIAGNOSE } from 'pages/Teachers/Insights/constants';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';

import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  selectRoot: {
    backgroundColor: '#F8F7FF',
    borderRadius: '10px',
    border: '1.5px solid #E0D9F7',
    padding: '4px 12px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#D1C4E9',
      backgroundColor: '#F3F0FF'
    },
    '&:before, &:after': {
      display: 'none'
    }
  },
  textFieldRoot: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F8F7FF',
      borderRadius: '10px',
      height: '50px',
      '& fieldset': {
        border: '1.5px solid #E0D9F7 !important',
        top: 0
      },
      '&:hover fieldset': {
        borderColor: '#D1C4E9 !important'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#6A61A1 !important',
        borderWidth: '1.5px !important'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 5px',
      color: '#6A61A1',
      fontSize: '1.1rem',
      height: '100%'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      '& legend': {
        display: 'none'
      }
    }
  },
  label: {
    color: '#6A61A1',
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: '8px',
    display: 'block'
  },
  selectIcon: {
    color: '#6A61A1',
    right: '12px'
  },
  inputText: {
    color: '#6A61A1',
    fontSize: '1.1rem',
    fontWeight: 400,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  placeholder: {
    color: '#7B74AB',
    opacity: 0.8
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&.Mui-disabled': {
      opacity: 0.5,
      color: '#7B74AB',
      backgroundColor: 'transparent'
    }
  }
}));

const StudentWorkDialogEdit = ({
  initialMastery,
  initialGrade,
  objectiveValueType,
  currentOptions,
  sectionId,
  onConfirm,
  setIsEdit,
  affectedStudentNames = []
}) => {
  const classes = useStyles();
  const { user } = useContext(UserContext);

  const [localMastery, setLocalMastery] = useState(initialMastery ?? '');
  const [localGrade, setLocalGrade] = useState(initialGrade ?? '');
  const [openMastery, setOpenMastery] = useState(false);
  const [openGrade, setOpenGrade] = useState(false);

  const isNumericInput = NUMBER_VALUE_TYPES.includes(objectiveValueType);
  const isInvalid = localMastery === '' && localGrade === '';

  const handleConfirm = () => {
    const updatedData = {
      overallMastery: localMastery,
      grade: localGrade
    };

    onConfirm({
      updatedData: updatedData
    });
  };

  const handleClose = () => {
    setIsEdit(false);

    trackMixpanelEvent(user.id, 'Bulk grade edit canceled', {
      feature: 'live classroom insights',
      view: 'Override Levels dialog',
      button: 'Cancel changes',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  return (
    <div className={styles.dialogContainer}>
      <div className={styles.header}>
        <span className={styles.studentTitle}>Override Levels</span>
        <IconButton onClick={() => handleClose()} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      {affectedStudentNames.length > 0 && (
        <div className={styles.affectedStudentsSection}>
          <span className={styles.affectedStudentsLabel}>
            Affected Students
          </span>
          <div className={styles.studentNamesContainer}>
            {affectedStudentNames.map((name, index) => (
              <div key={index} className={styles.studentName}>
                {name}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.inputGroup}>
        {/* Mastery Selection */}
        <FormControl className={classes.formControl}>
          <Select
            displayEmpty
            open={openMastery}
            onClose={() => setOpenMastery(false)}
            onOpen={() => setOpenMastery(true)}
            value={localMastery}
            onChange={(e) => setLocalMastery(e.target.value)}
            IconComponent={ExpandMoreIcon}
            className={classes.selectRoot}
            classes={{ select: classes.inputText, icon: classes.selectIcon }}
            renderValue={(selected) =>
              selected === '' || selected === UNABLE_TO_DIAGNOSE ? (
                <span className={classes.placeholder}>Select Level</span>
              ) : (
                MASTERY_LEVEL_TO_LABEL_MAPPER_SELECT[selected]
              )
            }
          >
            {Object.entries(MASTERY_LEVEL_TO_LABEL_MAPPER_SELECT).map(
              ([value, label]) => {
                if (value === UNABLE_TO_DIAGNOSE) {
                  return null;
                }

                return (
                  <MenuItem
                    key={value}
                    value={value}
                    className={classes.menuItem}
                  >
                    {label}
                  </MenuItem>
                );
              }
            )}
          </Select>
        </FormControl>

        {/* Grade/Score Selection or Input */}
        <div style={{ marginTop: '10px' }}>
          {isNumericInput && (
            <label className={styles.label}>Enter Score</label>
          )}

          {isNumericInput ? (
            <FormControl className={classes.formControl}>
              <TextField
                fullWidth
                variant="outlined"
                className={classes.textFieldRoot}
                placeholder={
                  objectiveValueType === 'number' ? '0-100' : 'Enter value'
                }
                value={localGrade}
                type="number"
                onChange={(e) => setLocalGrade(e.target.value)}
                InputLabelProps={{ shrink: false }}
              />
            </FormControl>
          ) : (
            <FormControl className={classes.formControl}>
              <Select
                displayEmpty
                open={openGrade}
                onClose={() => setOpenGrade(false)}
                onOpen={() => setOpenGrade(true)}
                value={localGrade}
                onChange={(e) => setLocalGrade(e.target.value)}
                IconComponent={ExpandMoreIcon}
                className={classes.selectRoot}
                classes={{
                  select: classes.inputText,
                  icon: classes.selectIcon
                }}
                renderValue={(selected) => {
                  if (selected === '')
                    return (
                      <span className={classes.placeholder}>Select Grade</span>
                    );

                  const match = currentOptions.find(
                    (opt) => String(opt.value) === String(selected)
                  );

                  return (
                    <div className={classes.inputText}>
                      <CheckGradeIcon
                        grade={selected}
                        objectiveValueType={objectiveValueType}
                      />
                      {match?.label || selected}
                    </div>
                  );
                }}
              >
                {currentOptions.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    className={classes.menuItem}
                  >
                    <CheckGradeIcon
                      grade={opt.value}
                      objectiveValueType={objectiveValueType}
                    />
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      <button
        disabled={isInvalid}
        onClick={handleConfirm}
        className={styles.confirmButton}
      >
        <ConfirmIcon /> CONFIRM
      </button>
    </div>
  );
};

export default StudentWorkDialogEdit;
